<template>
  <div class="members-and-shareholding-wrapper">
    <!-- Filter and Search controls -->
    <div class="bg-gray-pale px-3 py-2 border-b border-gray-light">
      <b-row class="align-items-center">
        <b-col md="6" sm="12" class="my-1">
          <div class="d-flex align-items-center">
            <label class="text-xs font-bold mb-0 mr-2 text-nowrap">Filter by Type:</label>
            <b-form-select
              v-model="entityType"
              :options="entityTypeOptions"
              size="sm"
              class="text-xs"
              @change="onEntityTypeChange"
            />
          </div>
        </b-col>
        <b-col md="6" sm="12" class="my-1 text-md-right text-left">
          <span v-if="loading" class="text-xs text-muted mr-2">
            <b-spinner small variant="primary" class="mr-1"></b-spinner>
            Loading...
          </span>
          <span class="text-xs font-bold text-gray" v-if="pagination.totalElements > 0">
            Total: {{ pagination.totalElements }} subscriber{{ pagination.totalElements === 1 ? '' : 's' }}
          </span>
        </b-col>
      </b-row>
    </div>

    <!-- Loading State -->
    <div v-if="loading && subscribers.length === 0" class="text-center py-4">
      <b-spinner small variant="primary"></b-spinner>
      <span class="ml-2 text-xs text-gray">Fetching subscribers...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && subscribers.length === 0" class="text-center py-4 bg-white">
      <span class="text-xs text-gray">No {{ displayMode === 'subscriptions' ? 'subscriptions' : 'subscribers' }} found.</span>
    </div>

    <!-- Subscriptions List Mode -->
    <div v-else-if="displayMode === 'subscriptions'">
      <table
        class="min-w-full leading-normal text-gray text-xs mb-2"
        style="border: solid 1px #dedede;"
        v-for="(subItem, sIdx) in subscriptions"
        :key="sIdx"
      >
        <thead>
          <tr style="border: solid 1px #dedede;">
            <td colspan="12" class="px-2 py-2 font-bold text-uppercase text-blue text-xs">
              {{ subItem.subscriber.name }}
              <b-badge class="bg-blue ml-1" v-if="subItem.totalNumberOfShares != null">
                {{ Number(subItem.totalNumberOfShares).toLocaleString() }}
              </b-badge>
            </td>
          </tr>
          <tr class="text-xs">
            <th class="px-2 py-1">Share Class</th>
            <th class="px-2 py-1 text-center">Shares</th>
            <th class="px-2 py-1 text-center">Paid</th>
            <th class="px-2 py-1 text-center">Not Paid</th>
          </tr>
        </thead>
        <tbody v-if="subItem.shares && subItem.shares.length > 0">
          <template v-for="(share, sIdx2) in subItem.shares">
            <tr :key="'share-' + sIdx2" style="border: solid 1px #dedede;">
              <td class="px-2 py-1 text-uppercase font-bold text-xs">
                <span v-if="share.nominalClass !== 'OTHER'">{{ share.nominalClass || share.displayName || 'ORDINARY' }}</span>
                <span v-else>{{ share.otherClassName || share.displayName || 'OTHER' }}</span>
              </td>
              <td class="px-2 py-1 text-center text-xs">
                <span v-if="!share.subClasses || share.subClasses.length === 0">
                  {{ Number(share.numberOfShares || 0).toLocaleString() }}
                </span>
              </td>
              <td class="px-2 py-1 text-center text-xs">
                <span v-if="!share.subClasses || share.subClasses.length === 0">
                  {{ Number(share.amountPaid || (share.numberOfSharesPaidInCash != null ? share.numberOfSharesPaidInCash : 0)).toLocaleString() }}
                </span>
              </td>
              <td class="px-2 py-1 text-center text-xs">
                <span v-if="!share.subClasses || share.subClasses.length === 0">
                  {{ Number(share.amountUnPaid || (share.numberOfUnPaidShares != null ? share.numberOfUnPaidShares : 0)).toLocaleString() }}
                </span>
              </td>
            </tr>
            <tr
              v-for="(subClass, scIdx) in (share.subClasses || [])"
              :key="'subclass-' + sIdx2 + '-' + scIdx"
            >
              <td class="px-2 py-1 text-capitalize text-xs">
                <span>Class {{ subClass.nominalClass }}</span>
              </td>
              <td class="px-2 py-1 text-center text-xs">
                <span>{{ Number(subClass.numberOfShares || 0).toLocaleString() }}</span>
              </td>
              <td class="px-2 py-1 text-center text-xs">
                <span>{{ Number(subClass.amountPaid || (subClass.numberOfSharesPaidInCash != null ? subClass.numberOfSharesPaidInCash : 0)).toLocaleString() }}</span>
              </td>
              <td class="px-2 py-1 text-center text-xs">
                <span>{{ Number(subClass.amountUnPaid || (subClass.numberOfUnPaidShares != null ? subClass.numberOfUnPaidShares : 0)).toLocaleString() }}</span>
              </td>
            </tr>
          </template>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="12" class="px-2 py-2 text-center text-xs text-muted">
              No share subscriptions specified.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Subscribers List via LegalPersons -->
    <div v-else>
      <LegalPersons class="p-1" :persons="subscribers" />
    </div>

    <!-- Pagination Controls -->
    <b-row class="mx-1 mt-2 align-items-center" v-if="pagination.totalPages > 1">
      <b-col cols="auto">
        <button
          type="button"
          @click="previousPage"
          :disabled="page <= 1 || loading"
          :class="(page > 1 && !loading) ? 'paginate-btn' : 'inert-btn'"
        >
          Previous
        </button>
      </b-col>
      <b-col cols="auto">
        <p class="paginate-details text-center mb-0">
          <b-badge class="bg-blue text-white">
            {{ page }} / {{ pagination.totalPages }}
          </b-badge>
        </p>
      </b-col>
      <b-col cols="auto">
        <button
          type="button"
          @click="nextPage"
          :disabled="page >= pagination.totalPages || loading"
          :class="(page < pagination.totalPages && !loading) ? 'paginate-btn' : 'inert-btn'"
        >
          Next
        </button>
      </b-col>
      <b-col class="d-none d-md-block"></b-col>
      <b-col cols="auto">
        <b-form-select
          v-model="pageSize"
          class="text-xs form-select border-0 paginate-details"
          @change="onPageSizeChange"
        >
          <b-select-option :value="10">10 / page</b-select-option>
          <b-select-option :value="20">20 / page</b-select-option>
          <b-select-option :value="50">50 / page</b-select-option>
          <b-select-option :value="100">100 / page</b-select-option>
        </b-form-select>
      </b-col>
      <b-col cols="auto">
        <p class="paginate-details text-center text-xs font-bold mb-0">
          <span>{{ ((page - 1) * pageSize) + 1 }}</span> -
          <span>{{ Math.min(page * pageSize, pagination.totalElements) }}</span>
          of {{ pagination.totalElements }}
        </p>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { api } from "@/app/services/api.service";
import LegalPersons from "@/views/home/commons/LegalPersons.vue";

export default {
  name: "MembersAndShareholdingSubscribers",
  components: {
    LegalPersons
  },
  props: {
    trackingNo: {
      type: String,
      required: true
    },
    initialEntityType: {
      type: String,
      default: ""
    },
    displayMode: {
      type: String,
      default: "subscribers", // 'subscribers' | 'subscriptions'
      validator: (val) => ["subscribers", "subscriptions"].includes(val)
    }
  },
  data() {
    return {
      subscribers: [],
      subscriptions: [],
      loading: false,
      entityType: this.initialEntityType || "",
      entityTypeOptions: [
        { value: "", text: "-- All Entity Types --" },
        { value: "INDIVIDUAL", text: "Individual" },
        { value: "CORPORATE", text: "Corporate Entity" },
        { value: "JOINT", text: "Joint Shareholder" },
        { value: "STOCK_EXCHANGE", text: "Securities Exchange" }
      ],
      page: 1, // 1-based request page
      pageSize: 10,
      pagination: {
        totalElements: 0,
        totalPages: 0,
        numberOfElements: 0,
        first: true,
        last: true
      }
    };
  },
  watch: {
    trackingNo: {
      immediate: true,
      handler(val) {
        if (val) {
          this.page = 1;
          this.fetchSubscribers();
        }
      }
    }
  },
  methods: {
    async fetchSubscribers() {
      if (!this.trackingNo) return;
      this.loading = true;
      try {
        const params = new URLSearchParams();
        params.set("trackingNo", this.trackingNo);
        params.set("page", String(this.page));
        params.set("pageSize", String(this.pageSize));
        if (this.entityType) {
          params.set("entityType", this.entityType);
        }

        const response = await api().get(
          `incorporation/companies/incorporation-draft-local/members-and-shareholding?${params.toString()}`
        );

        const data = response?.data?.data || {};
        const pageData = data?.members || {};

        const zeroBasedPage =
          pageData.number != null && Number.isFinite(Number(pageData.number))
            ? Number(pageData.number)
            : pageData.pageable?.pageNumber != null && Number.isFinite(Number(pageData.pageable.pageNumber))
              ? Number(pageData.pageable.pageNumber)
              : Math.max(0, this.page - 1);

        const currentPage = Math.max(1, zeroBasedPage + 1);
        const totalPages = Number(pageData.totalPages) || 0;
        this.page = currentPage;
        this.pagination = {
          totalElements: Number(pageData.totalElements) || 0,
          totalPages: totalPages,
          numberOfElements: Number(pageData.numberOfElements) || 0,
          first: pageData.first === true || currentPage <= 1,
          last: pageData.last === true || (totalPages > 0 && currentPage >= totalPages)
        };

        const content = Array.isArray(pageData.content) ? pageData.content : [];
        this.subscribers = content
          .map((item) => this.mapApiMemberToLegalPerson(item))
          .filter(Boolean);
        this.subscriptions = content
          .map((item) => this.mapApiMemberToSubscription(item))
          .filter(Boolean);
      } catch (error) {
        console.error("Failed to fetch members and shareholding subscribers:", error);
        this.subscribers = [];
        this.subscriptions = [];
      } finally {
        this.loading = false;
      }
    },
    mapApiMemberToLegalPerson(member) {
      if (!member) return null;
      const shareholder = member.shareholder || {};
      const entityType = shareholder.entityType || member.entityType || "INDIVIDUAL";

      return {
        ...shareholder,
        id: shareholder.id || member.id || null,
        entityType: entityType,
        individual: shareholder.individual || member.individual || null,
        corporate: shareholder.corporate || member.corporate || null,
        jointShareholder: shareholder.jointShareholder || member.jointShareholder || null,
        stockExchange: shareholder.stockExchange || member.stockExchange || null,
        name: shareholder.name || member.name || null,
        subscription: member.subscription || []
      };
    },
    mapApiMemberToSubscription(member) {
      if (!member) return null;
      const shareholder = member.shareholder || {};
      const subscription = Array.isArray(member.subscription) ? member.subscription : [];
      const totalShares = subscription.reduce(
        (sum, item) => sum + (Number(item.numberOfShares) || 0),
        0
      );

      return {
        subscriberId: shareholder.id || member.id || null,
        subscriber: {
          id: shareholder.id || member.id || null,
          name: shareholder.name || member.name || "Subscriber",
          entityType: shareholder.entityType || member.entityType || "INDIVIDUAL"
        },
        totalNumberOfShares: totalShares,
        shares: subscription
      };
    },
    onEntityTypeChange() {
      this.page = 1;
      this.fetchSubscribers();
    },
    onPageSizeChange() {
      this.page = 1;
      this.fetchSubscribers();
    },
    previousPage() {
      if (this.page > 1 && !this.loading) {
        this.page -= 1;
        this.fetchSubscribers();
      }
    },
    nextPage() {
      if (this.page < this.pagination.totalPages && !this.loading) {
        this.page += 1;
        this.fetchSubscribers();
      }
    }
  }
};
</script>

<style scoped>
.members-and-shareholding-wrapper {
  width: 100%;
}

.paginate-details {
  @apply px-3 py-2 bg-white my-2 text-xs text-gray;
}

.paginate-btn {
  @apply px-3 py-2 bg-white border border-white my-2 text-xs text-blue w-full;
  cursor: pointer;
}

.paginate-btn:hover {
  @apply border border-blue text-blue rounded-sm;
}

.inert-btn {
  @apply px-3 py-2 bg-white my-2 text-xs text-grey w-full;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
